import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { Brand } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  StyleRulesDialog,
  normalizeStyleRulesText,
  type StyleRulesValues,
} from "@/components/style-rules-dialog";
import { Button } from "@/components/ui/button";

function savedStyleRules(brand: Brand): StyleRulesValues {
  const designSystem = brand.brandDesignSystem;
  if (!designSystem || typeof designSystem !== "object") {
    return { styleRules: "", styleRulesFont: "" };
  }
  const values = designSystem as {
    styleRules?: unknown;
    styleRulesFont?: unknown;
  };
  return {
    styleRules: typeof values.styleRules === "string" ? values.styleRules : "",
    styleRulesFont: typeof values.styleRulesFont === "string" ? values.styleRulesFont : "",
  };
}

function designSystemWithStyleRules(brand: Brand, values: StyleRulesValues) {
  const current =
    brand.brandDesignSystem && typeof brand.brandDesignSystem === "object"
      ? { ...(brand.brandDesignSystem as Record<string, unknown>) }
      : {};
  return {
    ...current,
    styleRules: normalizeStyleRulesText(values.styleRules),
    styleRulesFont: normalizeStyleRulesText(values.styleRulesFont),
  };
}

/**
 * Standard (not beta-gated) Ads Express access to the same two-field editor
 * available in Brand Design System. This deliberately uses the normal brand
 * PATCH so the whole design-system blob is preserved, rather than the retired
 * rules-only endpoint.
 */
export function AdsExpressStyleRulesDialog({
  brandId,
  brand,
  // Kept as a source-compatible no-op for callers that still have the old
  // beta flag query. Standard access must never depend on this value.
  enabled: _legacyEnabled,
}: {
  brandId?: string;
  brand?: Brand;
  enabled?: boolean;
}) {
  if (!brand) {
    return null;
  }

  return (
    <AdsExpressStyleRulesControl
      key={brandId ?? brand.id}
      brand={brand}
      brandId={brandId ?? brand.id}
    />
  );
}

export function AdsExpressStyleRulesControl({
  brand,
  brandId,
  // This prop used to disable the beta editor. It is intentionally ignored.
  enabled: _legacyEnabled,
}: {
  brand: Brand;
  brandId?: string;
  enabled?: boolean;
}) {
  const { toast } = useToast();
  const incoming = savedStyleRules(brand);
  const targetBrandId = brandId ?? brand.id;
  const [savedValues, setSavedValues] = useState<StyleRulesValues>(incoming);
  const activeTargetRef = useRef<{ brandId: string; epoch: number }>({
    brandId: targetBrandId,
    epoch: 0,
  });

  // ID alone is not enough: A → B → A must not let the first A completion
  // overwrite the newly active A draft.
  if (activeTargetRef.current.brandId !== targetBrandId) {
    activeTargetRef.current = {
      brandId: targetBrandId,
      epoch: activeTargetRef.current.epoch + 1,
    };
  }

  useEffect(() => {
    setSavedValues(incoming);
  }, [targetBrandId, incoming.styleRules, incoming.styleRulesFont]);

  const saveMutation = useMutation({
    mutationFn: async ({
      brandId,
      values,
    }: {
      brandId: string;
      epoch: number;
      values: StyleRulesValues;
    }) => {
      const response = await apiRequest(
        "PATCH",
        `/api/brands/${brandId}`,
        {
          brandDesignSystem: designSystemWithStyleRules(brand, values),
          // The rules modal submits a full design-system blob. Require the
          // server to compare this snapshot before applying it so a concurrent
          // font/color edit cannot be silently erased.
          expectedBrandDesignSystem: brand.brandDesignSystem ?? null,
        },
      );
      const payload = await response.json() as
        | Brand
        | { brand?: Brand; styleRules?: string; styleRulesFont?: string };
      // The normal PATCH returns the brand directly. Accept the old wrapper
      // shape too so a rolling deployment cannot make a successful save look
      // like a client error.
      const responseBrand =
        payload && typeof payload === "object" && "brand" in payload && payload.brand
          ? payload.brand
          : payload as Brand;
      return {
        brand: responseBrand?.id ? responseBrand : {
          ...brand,
          brandDesignSystem: designSystemWithStyleRules(brand, values),
        },
        values,
      };
    },
    onSuccess: (data, variables) => {
      if (
        activeTargetRef.current.brandId !== variables.brandId
        || activeTargetRef.current.epoch !== variables.epoch
      ) {
        return;
      }
      setSavedValues(savedStyleRules(data.brand));
      queryClient.setQueryData(["/api/brands", variables.brandId], data.brand);
      queryClient.invalidateQueries({ queryKey: ["/api/brands"] });
      queryClient.invalidateQueries({ queryKey: ["/api/brands", variables.brandId] });
      toast({
        title: "Style Rules saved",
        description: `New ${data.brand.name || "brand"} Ads Express runs will snapshot these rules.`,
      });
    },
    onError: (error: Error, variables) => {
      // Keep the dialog's entered draft intact. A late error from a previous
      // brand target is intentionally ignored rather than shown for this one.
      if (
        activeTargetRef.current.brandId !== variables.brandId
        || activeTargetRef.current.epoch !== variables.epoch
      ) {
        return;
      }
      toast({
        title: "Failed to save Style Rules",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const save = async (values: StyleRulesValues) => {
    const target = {
      brandId: targetBrandId,
      epoch: activeTargetRef.current.epoch,
    };
    try {
      await saveMutation.mutateAsync({
        ...target,
        values,
      });
    } catch (error) {
      // A late rejection from a previous target is not an error for the
      // currently visible editor, and must not replace its draft with an
      // error state.
      if (
        activeTargetRef.current.brandId !== target.brandId
        || activeTargetRef.current.epoch !== target.epoch
      ) {
        return false;
      }
      throw error;
    }
    return (
      activeTargetRef.current.brandId === target.brandId
      && activeTargetRef.current.epoch === target.epoch
    );
  };

  return (
    <StyleRulesDialog
      values={savedValues}
      onSave={save}
      isSaving={saveMutation.isPending}
      bodyTestId="card-ads-express-style-rules"
      styleInputTestId="input-ads-express-style-rules"
      saveTestId="button-save-ads-express-style-rules"
      discardTestId="button-discard-ads-express-style-rules"
      trigger={
        <Button type="button" variant="outline" data-testid="button-open-style-rules">
          Style Rules
        </Button>
      }
    />
  );
}